import { For, Show, createEffect, createMemo, createSignal, untrack } from "solid-js";
import type { Exercise } from "../api/types";
import { t } from "../i18n";
import {
  MAX_DURATION_SEC,
  MAX_ROUNDS,
  durationToInput,
  formatDistance,
  formatSeconds,
  parseDistance,
  parseDuration,
  parseErrorText,
  positiveIntOrNull,
  rowType,
  type DurationMode,
} from "../lib/activity";
import DecimalField from "./DecimalField";

const STEPS_MIN = [5, 15] as const;

/** Text field with the smart duration input, a live "how it was read" hint and +5/+15 min. */
export function DurationField(props: {
  label: string;
  hint: string;
  optional?: boolean;
  mode: DurationMode;
  /** Stored value, seconds. */
  value: number | null | undefined;
  showErrors?: boolean;
  /** `bad` = text is present but cannot be read. */
  onChange: (sec: number | null, bad: boolean) => void;
}) {
  const [draft, setDraft] = createSignal(durationToInput(props.value, props.mode));
  const [touched, setTouched] = createSignal(false);
  const parsed = createMemo(() => parseDuration(draft(), props.mode));

  // External changes (type switch, steppers elsewhere) replace the text;
  // an unreadable draft is kept so the user can fix it.
  createEffect(() => {
    const v = props.value ?? null;
    const p = untrack(parsed);
    const cur = p.ok ? p.value : null;
    if (cur !== v && !(v === null && !p.ok && p.error !== "empty")) {
      setDraft(durationToInput(v, props.mode));
    }
  });

  function commit(text: string) {
    setDraft(text);
    const p = parseDuration(text, props.mode);
    props.onChange(p.ok ? p.value : null, !p.ok && p.error !== "empty");
  }

  function step(min: number) {
    const base = props.value ?? 0;
    const next = Math.min(MAX_DURATION_SEC, base + min * 60);
    commit(durationToInput(next, props.mode));
  }

  const error = createMemo(() => {
    const p = parsed();
    if (p.ok) return null;
    if (p.error === "empty" && props.optional) return null;
    if (!touched() && !props.showErrors) return null;
    return parseErrorText(p.error, props.mode);
  });
  const hint = createMemo(() => {
    const p = parsed();
    return p.ok ? formatSeconds(p.value) : "";
  });

  return (
    <div>
      <label class="label">
        {props.label} <span class="text-ink-faint">({props.hint})</span>
      </label>
      <div class="flex gap-2">
        <input
          class={`input min-w-0 flex-1 tabular-nums${error() ? " is-invalid" : ""}`}
          type="text"
          inputmode="text"
          enterkeyhint="done"
          autocomplete="off"
          autocorrect="off"
          spellcheck={false}
          placeholder={props.mode === "hm" ? t("DURATION_HM_HINT") : t("TIME_MS_HINT")}
          aria-label={props.label}
          aria-invalid={error() ? "true" : "false"}
          value={draft()}
          onInput={(e) => commit(e.currentTarget.value)}
          onBlur={() => setTouched(true)}
        />
        <For each={[...STEPS_MIN]}>
          {(m) => (
            <button type="button" class="step-btn" onClick={() => step(m)}>
              +{m}
            </button>
          )}
        </For>
      </div>
      <div class={`field-hint${error() ? " is-error" : ""}`} role={error() ? "alert" : undefined}>
        {error() ?? hint()}
      </div>
    </div>
  );
}

/** Whole meters, with the unit inside the field. */
export function DistanceField(props: {
  value: number | null | undefined;
  showErrors?: boolean;
  onChange: (m: number | null, bad: boolean) => void;
}) {
  const [draft, setDraft] = createSignal(props.value ? String(props.value) : "");
  const [touched, setTouched] = createSignal(false);
  const parsed = createMemo(() => parseDistance(draft()));

  createEffect(() => {
    const v = props.value ?? null;
    const p = untrack(parsed);
    const cur = p.ok ? p.value : null;
    if (cur !== v && !(v === null && !p.ok && p.error !== "empty")) {
      setDraft(v ? String(v) : "");
    }
  });

  const error = createMemo(() => {
    const p = parsed();
    if (p.ok || (!touched() && !props.showErrors)) return null;
    return parseErrorText(p.error, "distance");
  });
  const hint = () => {
    const p = parsed();
    return p.ok ? formatDistance(p.value) : "";
  };

  return (
    <div>
      <label class="label">{t("DISTANCE_M")}</label>
      <div class="relative">
        <input
          class={`input tabular-nums${error() ? " is-invalid" : ""}`}
          style={{ "padding-right": "40px" }}
          type="text"
          inputmode="numeric"
          enterkeyhint="done"
          autocomplete="off"
          placeholder="1500"
          aria-label={t("DISTANCE_M")}
          aria-invalid={error() ? "true" : "false"}
          value={draft()}
          onInput={(e) => {
            const text = e.currentTarget.value;
            setDraft(text);
            const p = parseDistance(text);
            props.onChange(p.ok ? p.value : null, !p.ok && p.error !== "empty");
          }}
          onBlur={() => setTouched(true)}
        />
        <span class="input-suffix">{t("UNIT_METERS")}</span>
      </div>
      <div class={`field-hint${error() ? " is-error" : ""}`} role={error() ? "alert" : undefined}>
        {error() ?? hint()}
      </div>
    </div>
  );
}

/** 1–10 scale; tapping the chosen value again clears it (the field is optional). */
export function IntensityPicker(props: {
  value: number | null | undefined;
  onChange: (v: number | null) => void;
}) {
  return (
    <div>
      <div class="mb-1.5 flex items-baseline justify-between">
        <span class="label !mb-0">
          {t("INTENSITY")} <span class="text-ink-faint">({t("OPTIONAL")})</span>
        </span>
        <Show when={props.value}>
          <span class="text-[13px] tabular-nums text-ink">
            {t("INTENSITY_OF").replace("{n}", String(props.value))}
          </span>
        </Show>
      </div>
      <div class="intensity-scale" role="group" aria-label={t("INTENSITY")}>
        <For each={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}>
          {(n) => (
            <button
              type="button"
              class={props.value === n ? "is-active" : ""}
              aria-pressed={props.value === n}
              onClick={() => props.onChange(props.value === n ? null : n)}
            >
              {n}
            </button>
          )}
        </For>
      </div>
    </div>
  );
}

function RoundsField(props: {
  value: number | null | undefined;
  onChange: (v: number | null) => void;
}) {
  return (
    <div class="w-1/3 min-w-[120px]">
      <label class="label">
        {t("ROUNDS")} <span class="text-ink-faint">({t("OPTIONAL")})</span>
      </label>
      <input
        class="input tabular-nums"
        type="text"
        inputmode="numeric"
        autocomplete="off"
        placeholder="—"
        aria-label={t("ROUNDS")}
        value={props.value ?? ""}
        onInput={(e) => {
          const digits = e.currentTarget.value.replace(/\D/g, "");
          const n = positiveIntOrNull(digits, MAX_ROUNDS);
          props.onChange(n);
          if (digits !== e.currentTarget.value || (digits !== "" && n == null)) {
            e.currentTarget.value = n == null ? "" : String(n);
          }
        }}
      />
    </div>
  );
}

/**
 * The value fields of one exercise row, chosen by its type.
 * Strength keeps sets / reps / weight; the other types never show kg or reps.
 */
export default function ActivityFields(props: {
  ex: Exercise;
  showErrors?: boolean;
  onPatch: (patch: Partial<Exercise>) => void;
}) {
  const type = () => rowType(props.ex);
  const markInvalid = (key: "duration" | "distance", bad: boolean) => ({
    invalid: { ...props.ex.invalid, [key]: bad || undefined },
  });

  return (
    <>
      <Show when={type() === "strength"}>
        <div class="grid grid-cols-3 gap-2">
          <div>
            <label class="label">{t("SETS")}</label>
            <input
              class="input"
              type="number"
              min="1"
              value={props.ex.sets}
              onInput={(e) => props.onPatch({ sets: Number(e.currentTarget.value) || 1 })}
            />
          </div>
          <div>
            <label class="label">{t("REPS")}</label>
            <input
              class="input"
              type="number"
              min="1"
              value={props.ex.reps}
              onInput={(e) => props.onPatch({ reps: Number(e.currentTarget.value) || 1 })}
            />
          </div>
          <div>
            <label class="label">
              {t("WEIGHT")} ({t("KG")})
            </label>
            <DecimalField
              class="input"
              value={props.ex.weight}
              min={0}
              decimals={2}
              placeholder="—"
              aria-label={`${t("WEIGHT")} (${t("KG")})`}
              onChange={(weight) => props.onPatch({ weight })}
            />
          </div>
        </div>
      </Show>

      <Show when={type() === "timed" || type() === "session"}>
        <DurationField
          label={t("DURATION")}
          hint={t("DURATION_HM_HINT")}
          mode="hm"
          value={props.ex.durationSec}
          showErrors={props.showErrors}
          onChange={(sec, bad) => props.onPatch({ durationSec: sec, ...markInvalid("duration", bad) })}
        />
      </Show>

      <Show when={type() === "session"}>
        <IntensityPicker
          value={props.ex.intensity}
          onChange={(intensity) => props.onPatch({ intensity })}
        />
        <RoundsField value={props.ex.rounds} onChange={(rounds) => props.onPatch({ rounds })} />
      </Show>

      <Show when={type() === "distance"}>
        <DistanceField
          value={props.ex.distanceM}
          showErrors={props.showErrors}
          onChange={(m, bad) => props.onPatch({ distanceM: m, ...markInvalid("distance", bad) })}
        />
        <DurationField
          label={t("TIME")}
          hint={t("TIME_MS_HINT")}
          optional
          mode="ms"
          value={props.ex.durationSec}
          showErrors={props.showErrors}
          onChange={(sec, bad) => props.onPatch({ durationSec: sec, ...markInvalid("duration", bad) })}
        />
      </Show>
    </>
  );
}
